import React, {useEffect, useRef, useState} from 'react';
import {Dialog} from "primereact/dialog";
import {useDispatch, useSelector} from "react-redux";
import {useTranslate} from "../../system/useTranslate";
import Workspace from "../../models/workspace/workspace";
import {setMessage, setWorkspace, setWorkspaceMembership, setWorkspaceMemberships} from "../../system/app-slice";
import {ListBox} from "primereact/listbox";
import Init, {CreateWorkspace} from "./init";
import WorkspaceMembership from "../../models/workspace-membership/workspace-membership";
import {TabMenu} from "primereact/tabmenu";
import Span from "../../system/components/span";
import {Divider} from "primereact/divider";
import Table from "../../system/components/table";
import {Checkbox} from "primereact/checkbox";
import OptionSet from "../../models/option-set/option-set";
import {InputText} from "primereact/inputtext";
import WorkspaceTeam from "../../models/workspace-team/workspace-team";
import {Dropdown} from "primereact/dropdown";
import DropdownItemTemplate from "../../system/components/dropdown-item-template";
import {Button} from "primereact/button";
import {farsiDigitsToInt, isPast, parseDate} from "../../system/utils";
import WorkspaceInvitation from "../../models/workspace-invitation/workspace-invitation";
import {DataView} from "primereact/dataview";
import useWorkspaceMembership from "../../models/workspace-membership/use-workspace-membership";
import {useWorkspace} from "../../models/workspace/use-workspace";
import {useWorkspaceRole} from "../../models/workspace-role/use-workspace-role";
import {useSubscription} from "../../models/subscription/use-subscription";
import {useSubscriptionPlan} from "../../models/subscription-plan/use-subscription-plan";
import {useWorkspaceTeam} from "../../models/workspace-team/use-workspace-team";
import {useOptionSet} from "../../models/option-set/use-option-set";
import DataFieldItem from "../../system/components/data-field-item";
import DatatableEmptyMessage from "../../system/components/datatable-empty-message";
import {getEffectivePermissions} from "../../system/workspace-permissions";

function SelectWorkspaceDialog({visible, setVisible}) {
    const {config, workspaceMembershipsData, workspaceMembershipData} = useSelector((state) => state.app);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState();
    const translate = useTranslate();

    useEffect(()=>{
        setSelectedItem(workspaceMembershipData?.id);
    },[workspaceMembershipData?.id])

    return (
        <Dialog maximizable header={translate("select-workspace")} className={`w-8 scrollbar-hide h-30rem ${config.dir}`} visible={visible} onHide={()=>{setVisible(false)}}>
            <div className="flex w-full h-full border-1 border-round border-gray-200">
                <div className="w-3">
                    <ListBox itemTemplate={ItemTemplate} value={selectedItem} onChange={(e)=>{setSelectedItem(e.value)}}  className="border-none" options={workspaceMembershipsData?.map(e=>({label:e.workspace.name, value:e.id})).concat({label:translate("new-workspace"), value:0})}/>
                </div>
                <div className="flex-1 border-right-1 border-gray-200">
                    <ItemContent value={selectedItem} setVisible={setVisible}/>
                </div>
            </div>
        </Dialog>
    );
}

function ItemTemplate(option) {
    return(
        <div className="flex align-items-center justify-content-between border-round">
            <span className="text-sm">{option.label}</span>
            {option.value>0&&<i className="pi pi-chevron-left text-xs"/>}
        </div>
    )
}

function ItemContent({value, visible, setVisible}) {
    const {config, workspaceMembershipsData} = useSelector((state) => state.app);
    const translate = useTranslate();
    const dispatch = useDispatch();
    const {user} = useSelector(state => state.auth);
    const workspaceMembership = useWorkspaceMembership();
    const [activeIndex, setActiveIndex] = useState(0);
    let currentWorkspaceMembership = workspaceMembershipsData?.find(e=>e.id===value);
    const effectivePermissions = getEffectivePermissions(
        currentWorkspaceMembership?.user_roles || [],
        undefined,
        undefined,
        currentWorkspaceMembership?.workspace?.workspaceStatus || "active",
    );
    const can = (permission) => (
        effectivePermissions.includes("*") || effectivePermissions.includes(permission)
    );
    const availableTabs = [
        {label: translate("settings"), icon: 'pi pi-cog pl-1', className:"text-xs", view:Settings},
        {label: translate("teams-invite-members"), icon: 'pi pi-users pl-1', className:"text-xs", view:ManageMembers, permission:"modify-workspace-members"},
        {label: translate("access-levels"), icon: 'pi pi-shield pl-1', className:"text-xs", view:AccessLevels, permission:"modify-access-control"},
        {label: translate("subscription"), icon: 'pi pi-verified pl-1', className:"text-xs", view:SubscriptionContent, permission:"canManageBilling"},
    ].filter((item) => !item.permission || can(item.permission));
    const items = availableTabs.map(({view, permission, ...item}) => item);
    const ActiveTabContent = availableTabs[activeIndex]?.view || Settings;

    useEffect(() => {
        setActiveIndex(0);
    }, [value, availableTabs.length]);

    if (value>0){
        return(
            <div className="flex flex-column h-full">
                <div>
                    <div className="flex gap-2 align-items-center p-1">
                        <span className="font-bold text-lg p-1">{currentWorkspaceMembership.workspace.name}</span>
                        {!currentWorkspaceMembership.isDefault&&<span className="text-2xs p-1 border-round bg-primary cursor-pointer" onClick={()=>{workspaceMembership.updateDefaultWorkspaceMembership(currentWorkspaceMembership.documentId)}}>{translate("set-as-default")}</span>}
                    </div>
                </div>
                <div className="flex-1 overflow-hidden">
                    <TabMenu activeIndex={activeIndex} model={items} onTabChange={(e)=>{setActiveIndex(e.index); }}/>
                    <ActiveTabContent workspaceData={currentWorkspaceMembership.workspace} canEdit={can("modify-workspace-settings")}/>
                </div>
                {/*<div>footer</div>*/}
            </div>
        )
    }
    else {
        return (
            <div className="w-full h-full flex flex-column gap-1">
                <CreateWorkspace onCreated={()=>{setVisible?.(false)}}/>
            </div>
        )
    }
}

function Settings({workspaceData, canEdit}) {
    const translate = useTranslate();
    const workspace = useWorkspace();

    return(
        <div className="p-2">
            <div className="flex flex-column">
                <span className="font-bold text-lg">{translate("general")}</span>
                <DataFieldItem disabled={!canEdit} label={translate("workspace-name")} value={workspaceData?.name} onKeyDownEnter={(e)=>{workspace.updateWorkspace(workspaceData?.documentId,{name:e.value})}}/>
                <Divider className="m-1 mr-0 ml-0"/>
            </div>
        </div>
    )
}

function ManageMembers({workspaceData}) {
    return(
        <div className="h-full overflow-hidden pb-6 pt-1">
            <div className="h-full flex flex-column gap-3 overflow-auto p-2">
                <Members workspaceData={workspaceData}/>
                <InviteMember workspaceData={workspaceData}/>
                <Teams workspaceData={workspaceData}/>
            </div>
        </div>
    )
}

function AccessLevels({workspaceData}) {
    const [selectedItem, setSelectedItem] = useState();
    const translate = useTranslate();
    const [workspaceRolesData, setWorkspaceRolesData] = useState([]);
    const workspaceRole = useWorkspaceRole();

    useEffect(()=>{
        (async () => {
            const [_workspaceRolesData] = await Promise.all([
                workspaceRole.getWorkspaceRoles(workspaceData.id),
            ]);
            setWorkspaceRolesData(_workspaceRolesData);
        })();
    },[workspaceData])

    return(
        <div className="flex w-full h-full">
            <div className="w-3">
                <ListBox itemTemplate={ItemTemplate} value={selectedItem?.value} onChange={(roleItem)=>{setSelectedItem(workspaceRolesData?.find(r=>r.value===roleItem.value))}}  className="border-none" options={workspaceRolesData?.concat({label:translate("add-new-role"), value:0})}/>
            </div>
            <div className="flex-1 border-right-1 border-gray-200">
                <RoleItemContent value={selectedItem??workspaceRolesData?.[1]}/>
            </div>
        </div>
    )
}

function RoleItemContent({value}) {
    const translate = useTranslate();
    const [permissions,setPermissions] = useState(value?.permissions);

    useEffect(()=>{
        setPermissions(value?.permissions)
    },[value])

    return(
        <div className="p-2 flex flex-column gap-2">
            {permissions&&Object.entries(permissions).map(([k,v])=>(
                <div className="rtl flex gap-1 align-items-center">
                    <Checkbox name={k} checked={v} onChange={(e)=>{setPermissions({...permissions, [k]:e.checked})}}/>
                    <label className="text-xs" htmlFor={k}>{translate(k)}</label>
                </div>
            ))}
        </div>
    )
}

function SubscriptionContent({workspaceData}) {
    const translate = useTranslate();
    const [currentPlan, setCurrentPlan] = useState();
    const [plans, setPlans] = useState();
    const {getCurrentSubscription} = useSubscription();
    const {getSubscriptionPlans} = useSubscriptionPlan();

    useEffect(() => {
        (async () => {
            const [subscriptionData, plansData] = await Promise.all([
                getCurrentSubscription(workspaceData.id),
                getSubscriptionPlans()
            ]);
            setCurrentPlan([subscriptionData.subscriptionPlanData]);
            setPlans(plansData);
        })();

    }, [getCurrentSubscription, getSubscriptionPlans, workspaceData.id]);



    const SubscriptionItemTemplate = (option)=>{
        const KeyValueItem = (k,v)=>{
            return <div className="flex gap-1 p-1 pl-2 pr-2"><span>{translate(k)}</span><span className="font-bold">{v}</span></div>
        }
        const displayPrice = option?.planPrice || option?.prices?.find((price) => price.isDefault) || option?.prices?.[0];
        return(
            <div className="w-full p-1">
                <div className="border-round surface-200 p-2 flex flex-wrap w-full text-sm">
                    {KeyValueItem("product-name", option.name)}
                    {KeyValueItem("price", displayPrice?.amount ?? translate("billing.value.contractual"))}
                    {option.expireAt&&KeyValueItem("expire-date", parseDate(option.expireAt))}
                </div>
            </div>
        )
    }


    return(
        <div className="flex flex-column w-full h-full">
            <div className="p-2">
                <span className="font-bold text-lg">{translate("subscription-status")}</span>
                <DataView value={currentPlan} itemTemplate={SubscriptionItemTemplate}/>
            </div>
            <div className="flex-1 p-2">
                <span className="font-bold text-lg">{translate("products")}</span>
                <DataView emptyMessage={<DatatableEmptyMessage/>} value={plans} itemTemplate={SubscriptionItemTemplate}/>
            </div>

        </div>
    )
}

function Teams({workspaceData}) {
    const translate = useTranslate();
    const [teams, setTeams] = useState();
    const [selectedTeams, setSelectedTeams] = useState([]);
    const [otherTeams, setOtherTeams] = useState([]);
    const [otherTeam, setOtherTeam] = useState();
    const workspaceTeam = useWorkspaceTeam();
    const optionSet = useOptionSet();

    useEffect(() => {
        (async () => {
            const [workspaceTeamData, optionSetsData] = await Promise.all([
                workspaceTeam.getWorkspaceTeams(workspaceData.id),
                optionSet.getOptionSets()
            ]);
            setTeams(optionSetsData.teams);
            setOtherTeams(workspaceTeamData);
        })();
    }, [workspaceData]);




    return(
        <div className="flex flex-column mt-4 p-1 gap-2">
            <span className="font-bold text-lg">{translate("teams")}</span>
            <div className="flex flex-wrap row-gap-1 align-items-center mt-2">
                {teams?.concat(otherTeams).map(t => (
                    <div key={t} className="w-full lg:w-3 rtl flex align-items-center gap-2">
                        <Checkbox inputId={t} value={t} checked={selectedTeams.includes(t)} onChange={(e) => {
                            if (e.checked) {
                                setSelectedTeams([...selectedTeams, t]);     // add
                            } else {
                                setSelectedTeams(selectedTeams.filter(item => item !== t)); // remove
                            }
                        }}
                        />
                        <label className="text-700 text-xs" htmlFor={t}>{translate(t)}</label>
                    </div>
                ))}
            </div>
            {
                selectedTeams.includes("other")&&(
                    <div className="w-full mt-2">
                        <div className="p-inputgroup ltr w-4">
                            <span className="p-inputgroup-addon cursor-pointer" onClick={()=>{if (otherTeam)setOtherTeams([...otherTeams, otherTeam]);setSelectedTeams([...selectedTeams, otherTeam]); setOtherTeam("")}}>
                                <i className="pi pi-plus"/>
                            </span>
                            <InputText placeholder={translate("team-name-placeholder")} className="p-1 rtl text-sm" value={otherTeam} onChange={(e)=>{setOtherTeam(e.target.value)}}/>
                        </div>

                    </div>
                )
            }

        </div>
    )
}

function Members({workspaceData}) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const {user} = useSelector(state => state.auth);
    const [data, setData] = useState();
    const [loading, setLoading] = useState(false);

    useEffect(()=>{
        getWorkspaceMemberships();
    },[workspaceData])

    const getWorkspaceMemberships = async ()=>{
        setLoading(true);
        try {
            const workspaceMembership = new WorkspaceMembership();
            const query = {
                filters: {
                    $and:[
                        {workspace:{$eq: workspaceData.id}},
                        // {members: {id:{$eq: user.id}}}
                    ],
                },
                populate:"*"
            }
            const _data = await workspaceMembership.getAll(query);
            setData(()=>{

                return _data.data.map(d=>({name:d.member.fullname, role:d.role.slug}))
            });
            setLoading(false);
            return _data;
        }catch (e) {
            setLoading(false);
        }

    }

    return(
        <div className="flex flex-column p-1 mt-4 gap-2">
            <span className="font-bold text-lg ">{translate("members")}</span>
            <Table showHeader={false} showFooter={false} value={data} loading={loading} showPagination={false} style={{height:"auto", border:"1px solid var(--gray-200)"}}/>
        </div>
    )
}

function InviteMember({workspaceData}) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const {user} = useSelector(state => state.auth);

    const [workspaceTeams, setWorkspaceTeams] = useState();
    const [inviteData, setInviteData] = useState({role:"viewer"});
    const [submittingInvite, setSubmittingInvite] = useState(false);
    const inviteLockRef = useRef(false);
    const roleOptions = [
        {label:translate("workspace-member"), value:"viewer"},
        {label:translate("guest"), value:"guest"},
    ];



    useEffect(()=>{
        (async () => {
            await getWorkspaceTeams();
        })();

    },[workspaceData])

    const getWorkspaceTeams = async ()=>{
        try {
            const workspaceTeam = new WorkspaceTeam();
            const query = {
                filters: {
                    $and:[
                        {workspace:{$eq: workspaceData.id}},
                        // {members: {id:{$eq: user.id}}}
                    ],
                },
                populate:"*"
            }
            const _data = await workspaceTeam.getAll(query);
            setWorkspaceTeams(()=>{
                return _data.data.map(d=>({label:d.name, value:d.id}))
            });
            return _data;
        }catch (e) {
        }

    }

    const createWorkspaceInvite = async (_inviteData)=>{
        if (submittingInvite || inviteLockRef.current) return;
        inviteLockRef.current = true;
        setSubmittingInvite(true);
        _inviteData = {..._inviteData, workspaceId:workspaceData.id}
       try {
           await new WorkspaceInvitation().invite(_inviteData);
           const workspaceMembership = new WorkspaceMembership();

           const query = {
               filters: {
                   $and:[
                       {member:{$eq: user.id}},
                       // {members: {id:{$eq: user.id}}}
                   ],
               },
               populate:"*"
           }
           const _data = await workspaceMembership.getAll(query);
           dispatch(setWorkspaceMemberships(_data?.data));
           dispatch(setWorkspaceMembership((_data?.data.find(e=>e.isDefault))));
           dispatch(setMessage({severity:"info", content:translate("workspace-invitation.sent")}));
       }catch (e) {
           dispatch(setMessage({severity:"error", content:translate(e?.message || "workspace-invitation.error.send")}));
       }finally {
           inviteLockRef.current = false;
           setSubmittingInvite(false);
       }
    }

    return(
        <form className="flex flex-column p-1 gap-2" onSubmit={(event) => {
            event.preventDefault();
            createWorkspaceInvite(inviteData);
        }}>
            <span className="font-bold text-lg">{translate("invite-member")}</span>
            <div className="flex flex-wrap">
                <div className="flex flex-column w-2 p-1">
                    <label className="text-xs">{translate("mobile")}</label>
                    <InputText className="w-full text-xs p-3" onChange={(e)=>{setInviteData({...inviteData, phonenumber:farsiDigitsToInt(e.target.value)})}}/>
                </div>
                <div className="flex flex-column w-3 p-1">
                    <label className="text-xs">{translate("fullname")}</label>
                    <InputText className="w-full text-xs p-3" onChange={(e)=>{setInviteData({...inviteData, fullname:e.target.value})}}/>
                </div>
                <div className="flex flex-column w-2 p-1">
                    <label className="text-xs">{translate("role")}</label>
                    <Dropdown value={inviteData?.role} onChange={(e)=>{setInviteData({...inviteData, role:e.value})}} className="w-full text-xs h-full" panelClassName="rtl text-xs" options={roleOptions}/>
                </div>
                <div className="flex flex-column w-3 p-1">
                    <label className="text-xs">{translate("team")}</label>
                    <Dropdown value={inviteData?.team} onChange={(e)=>{setInviteData({...inviteData, team:e.value})}} className="w-full text-xs h-full" panelClassName="text-xs" itemTemplate={DropdownItemTemplate} valueTemplate={DropdownItemTemplate} options={workspaceTeams||[]}/>
                </div>

                {/*<div className="flex flex-column w-8 p-1">*/}
                {/*    <label className="text-xs">{translate("invite-message")}</label>*/}
                {/*    <InputText className="w-full" onChange={(e)=>{setInviteData({...inviteData, message:e.target.value})}}/>*/}
                {/*</div>*/}
                <div className="flex flex-column w-2 p-1">
                    <Button type="submit" className="mt-3 text-xs white-space-nowrap h-full" label={translate("send-invite")} disabled={submittingInvite} loading={submittingInvite}/>
                </div>

            </div>

        </form>
    )
}



export default SelectWorkspaceDialog;
